import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import { useTheme } from '@/hooks/useTheme';
import { usePermissions } from '@/hooks/usePermissions';
import { PERM } from '@/auth/permissions';
import { useAISettings } from '@/services/aiCourses';
import mascotHappyJump from '../../../public/tery/tery_happy_jump.png';
import teriAvatar from '../../../public/tery/teri_ai_avatar_v2.png';
import welcomeSwirl from '../../../public/gradients/welcome-swirl.svg';

const EyeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 16.5C14.4853 16.5 16.5 14.4853 16.5 12C16.5 9.51472 14.4853 7.5 12 7.5C9.51472 7.5 7.5 9.51472 7.5 12C7.5 14.4853 9.51472 16.5 12 16.5Z" fill="currentColor" />
    <path opacity="0.5" d="M21.87 11.5C21.23 10.39 17.71 4.82001 11.73 5.00001C6.20001 5.14001 3.00001 10 2.13001 11.5C2.04224 11.6486 1.99609 11.8186 1.99609 11.992C1.99609 12.1653 2.04224 12.3353 2.13001 12.484C2.76001 13.594 6.13001 19 12.02 19H12.27C17.8 18.86 21 13.984 21.87 12.484C21.9582 12.336 22.0046 12.166 22.0046 11.992C22.0046 11.818 21.9582 11.648 21.87 11.5Z" fill="currentColor" />
  </svg>
);

const PlusCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM16 12.75H12.75V16C12.75 16.41 12.41 16.75 12 16.75C11.59 16.75 11.25 16.41 11.25 16V12.75H8C7.59 12.75 7.25 12.41 7.25 12C7.25 11.59 7.59 11.25 8 11.25H11.25V8C11.25 7.59 11.59 7.25 12 7.25C12.41 7.25 12.75 7.59 12.75 8V11.25H16C16.41 11.25 16.75 11.59 16.75 12C16.75 12.41 16.41 12.75 16 12.75Z" fill="currentColor" />
  </svg>
);

const CourseCreationIntroPage: React.FC = () => {
  const { theme } = useTheme();
  const { can } = usePermissions();
  const { data: aiSettings } = useAISettings();
  const aiReady =
    can(PERM.COURSE_AI_GENERATE) && !!aiSettings?.available && !!aiSettings?.enabled;
  const aiTitle = aiSettings?.available && !aiSettings?.enabled
    ? 'Enable AI in Settings'
    : 'Coming soon';

  return (
    <motion.div className="relative min-h-[calc(100vh-200px)] flex flex-col" {...slideUp}>
      <div className="relative z-10 flex flex-col gap-9 flex-1 px-2 md:px-0">
        {/* Hero text section */}
        <div className="flex flex-col gap-10 md:gap-12">
          {/* Title with decorative swirl */}
          <div className="relative inline-block">
            <img
              src={welcomeSwirl}
              alt=""
              className={`absolute -top-2 -left-2 w-[300px] md:w-[360px] h-auto rotate-[6deg] pointer-events-none select-none ${
                theme === 'dark' ? 'opacity-0' : ''
              }`}
            />
            <h1 className="text-[40px] md:text-[70px] leading-[45px] md:leading-[55px] uppercase tracking-[0px]">
              <span className="font-extrabold text-primary-600 dark:text-primary-350">Welcome</span>
              <span className="font-medium text-text-primary"> to the</span>
            </h1>
            <p className="text-[32px] md:text-[70px] leading-[45px] md:leading-[67px] font-medium text-text-primary capitalize mt-1 md:mt-0">
              Course Creation Space!
            </p>
          </div>

          {/* Subtitle */}
          <p className="text-base md:text-xl leading-7 text-text-primary max-w-[651px]">
            Before you start, please choose if you want to continue working on
            one of your drafts or create a new course from scratch!
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
          {/* Review my drafts */}
          <Link
            to="/courses?status=draft"
            className="flex items-center gap-3 px-6 py-2 rounded-full bg-primary-600 dark:bg-primary-350 hover:bg-primary-700 dark:hover:bg-primary-400 shadow-[0px_8px_16px_0px_rgb(var(--color-primary) / 0.24)] dark:shadow-[0px_8px_24px_0px_rgb(var(--color-primary) / 0.32)] transition-colors"
          >
            <EyeIcon className="w-6 h-6" />
            <span className="text-base font-medium whitespace-nowrap">Review my drafts</span>
          </Link>

          {/* Create from scratch */}
          <Link
            to="/courses/new"
            className="flex items-center gap-3 px-6 py-2 rounded-full bg-white border border-primary-100 hover:border-primary-200 text-primary-600 shadow-[0px_8px_16px_0px_rgb(var(--color-primary) / 0.24)] dark:shadow-[0px_8px_24px_0px_rgb(var(--color-primary) / 0.32)] transition-colors min-w-[252px]"
          >
            <PlusCircleIcon className="w-6 h-6" />
            <span className="text-base font-medium whitespace-nowrap">Create from scratch</span>
          </Link>

          {/* Create with Teri (AI) */}
          {aiReady ? (
            <Link
              to="/courses/ai"
              className="flex items-center gap-1.5 px-6 py-2 rounded-full bg-primary-600 dark:bg-primary-350 hover:bg-primary-700 dark:hover:bg-primary-400 shadow-[0px_8px_16px_0px_rgb(var(--color-primary) / 0.24)] dark:shadow-[0px_8px_24px_0px_rgb(var(--color-primary) / 0.32)] transition-colors"
            >
              <img
                src={teriAvatar}
                alt="Teri AI"
                className="w-[26px] h-[26px] rounded-full shadow-[7px_12px_7px_0px_rgba(0,0,0,0.11)] object-contain"
              />
              <span className="text-base font-medium whitespace-nowrap">Create with Teri (AI)</span>
            </Link>
          ) : (
            <button
              className="flex items-center gap-1.5 px-6 py-2 rounded-full bg-primary-600 dark:bg-primary-350 hover:bg-primary-700 dark:hover:bg-primary-400 shadow-[0px_8px_16px_0px_rgb(var(--color-primary) / 0.24)] dark:shadow-[0px_8px_24px_0px_rgb(var(--color-primary) / 0.32)] transition-colors cursor-not-allowed opacity-80"
              title={aiTitle}
              disabled
            >
              <img
                src={teriAvatar}
                alt="Teri AI"
                className="w-[26px] h-[26px] rounded-full shadow-[7px_12px_7px_0px_rgba(0,0,0,0.11)] object-contain"
              />
              <span className="text-base font-medium whitespace-nowrap">Create with Teri (AI)</span>
            </button>
          )}
        </div>

        {/* Mascot - aligned to right */}
        <div className="flex items-center sm:justify-end justify-center w-full mt-auto">
          <img
            src={mascotHappyJump}
            alt="Happy mascot"
            className="w-[200px] md:w-[290px] h-auto object-contain pointer-events-none select-none"
          />
        </div>
      </div>
    </motion.div>
  );
};

export default CourseCreationIntroPage;
